import { FACILITY_BY_ID, GOOD_BY_ID } from './data.js';
import { advanceToNextOpportunity, getDerived, tick } from './simulation.js';

export const WORK_SHIFT_MAX_MINUTES = 360;
const WORK_SHIFT_MAX_SWEEPS = 30;

function urgentOffer(state, derived) {
  return derived.meetings.pending.find((offer) => offer.canAccept
    && offer.expiresMinute != null
    && offer.expiresMinute - state.timeMinutes <= 1);
}

// Mirror the ordinary advance helper's boundaries only to truncate its final
// step at the shift limit. All actual economy work still uses that helper or
// the same simulation tick; no alternative prices, rewards or trade rules run.
function nextBoundary(state, derived, preserveOffers) {
  const now = state.timeMinutes;
  const events = [now + 120];
  for (const visitor of state.traffic) {
    if (visitor.status === 'scheduled' && visitor.arrivalMinute > now) events.push(visitor.arrivalMinute);
  }
  for (const order of state.orders) {
    if (order.status === 'in-transit' && order.arrivalMinute > now) events.push(order.arrivalMinute);
  }
  for (const plot of state.plots) {
    if (plot.construction) events.push(now + plot.construction.phaseDurationMinutes - plot.construction.phaseProgressMinutes);
  }
  if (preserveOffers) for (const offer of derived.meetings.pending) {
    if (offer.canAccept && offer.expiresMinute != null) events.push(offer.expiresMinute - 1);
  }
  return Math.max(now + 1, Math.min(...events));
}

function constructionChanges(state, initial) {
  const completedConstruction = [];
  const constructionProgress = [];
  for (const [plotIndex, before] of initial) {
    const plot = state.plots.find((item) => item.index === plotIndex);
    const name = FACILITY_BY_ID[before.facilityId].name;
    if (!plot.construction && plot.facilityId === before.facilityId) {
      completedConstruction.push({ plotIndex, facilityId: before.facilityId, name });
    } else if (plot.construction) {
      const after = plot.construction;
      const advancedMinutes = (after.phase - before.phase) * after.phaseDurationMinutes
        + after.phaseProgressMinutes - before.phaseProgressMinutes;
      if (advancedMinutes > 0) constructionProgress.push({
        plotIndex, facilityId: before.facilityId, name,
        fromPhase: before.phase, toPhase: after.phase,
        phaseName: after.phaseName, advancedMinutes,
      });
    }
  }
  return { completedConstruction, constructionProgress };
}

function summarizeGoods(entries, kinds) {
  const goods = new Map();
  for (const entry of entries) {
    if (!kinds.includes(entry.kind) || !entry.accepted || !entry.goodId || !entry.qty) continue;
    const item = goods.get(entry.goodId) || { goodId: entry.goodId, name: GOOD_BY_ID[entry.goodId]?.name || entry.goodId, qty: 0, totalCents: 0, grossProfitCents: 0, costBasisCents: 0, costKnown: true };
    item.qty += entry.qty;
    item.totalCents += Math.abs(entry.totalCents || 0);
    if (kinds.includes('sell')) {
      item.costKnown &&= Number.isInteger(entry.costBasisCents) && Number.isInteger(entry.grossProfitCents);
      item.costBasisCents += entry.costBasisCents || 0;
      item.grossProfitCents += entry.grossProfitCents || 0;
    } else item.costBasisCents += Math.abs(entry.totalCents || 0);
    goods.set(entry.goodId, item);
  }
  return [...goods.values()];
}

// One deliberate shift follows stock through to a customer outcome. Buying
// alone is preparation, not the payoff. Optional barter runs alongside normal
// commerce and may expire naturally; deliberate bargaining can preserve it.
export function runWorkShift(state, { passOfferId = null, preserveOffers = false, durationMinutes = WORK_SHIFT_MAX_MINUTES, aggregate = false } = {}) {
  if (state.opening.status !== 'completed') throw new Error('Complete Eli Mercer’s opening fur trade first');
  if (![WORK_SHIFT_MAX_MINUTES, 1440].includes(durationMinutes)) throw new Error('Choose a customer shift or one trading day');
  const startMinute = state.timeMinutes;
  const startCash = state.cashCents;
  const startTransactions = state.transactions;
  const initialConstruction = new Map(state.plots.filter((plot) => plot.construction)
    .map((plot) => [plot.index, { ...plot.construction }]));
  const entries = [];
  let stopReason = 'limit';
  let expiringOffer = null;

  for (let sweep = 0; sweep < (aggregate ? 180 : WORK_SHIFT_MAX_SWEEPS); sweep += 1) {
    const derived = getDerived(state);
    const urgent = urgentOffer(state, derived);
    if (preserveOffers && urgent && urgent.id !== passOfferId) {
      expiringOffer = { id: urgent.id, vendorName: urgent.vendorName, expiresMinute: urgent.expiresMinute };
      stopReason = 'offer';
      break;
    }
    const remaining = durationMinutes - (state.timeMinutes - startMinute);
    if (remaining <= 0) break;
    const firstEntry = state.nextLedgerId;
    // A second, explicitly labelled work action can let this particular
    // last-minute offer pass. The existing expiry system records its outcome.
    // Expiring at its deadline avoids crossing a later offer's last minute.
    if (preserveOffers && urgent && urgent.id === passOfferId) tick(state, Math.min(remaining, Math.max(1, urgent.expiresMinute - state.timeMinutes)));
    else if (nextBoundary(state, derived, preserveOffers) - state.timeMinutes > remaining) tick(state, remaining);
    else if (preserveOffers) advanceToNextOpportunity(state);
    else tick(state, nextBoundary(state, derived, false) - state.timeMinutes);
    const additions = state.ledger.filter((entry) => Number(entry.id.slice('ledger-'.length)) >= firstEntry);
    entries.push(...additions.map((entry) => ({ ...entry })));

    const newlyUrgent = urgentOffer(state, getDerived(state));
    if (preserveOffers && newlyUrgent) {
      expiringOffer = { id: newlyUrgent.id, vendorName: newlyUrgent.vendorName, expiresMinute: newlyUrgent.expiresMinute };
      stopReason = 'offer';
      break;
    }
    const construction = constructionChanges(state, initialConstruction);
    if (!aggregate && (construction.completedConstruction.length
      || construction.constructionProgress.some((item) => item.fromPhase !== item.toPhase))) {
      stopReason = 'construction';
      break;
    }
    // Small landing fees join the receipt while we keep seeking a customer
    // purchase. A service visit alone should not turn a shift into one click
    // per quarter; construction, deliveries and the time limit still stop it.
    if (!aggregate && additions.some((entry) => ['sell', 'meeting-sell'].includes(entry.kind))) {
      stopReason = 'trade';
      break;
    }
    if (!aggregate && additions.some((entry) => entry.kind === 'delivery')) {
      stopReason = 'delivery';
      break;
    }
  }

  const soldGoods = summarizeGoods(entries, ['sell', 'meeting-sell']);
  const purchasedGoods = summarizeGoods(entries, ['buy', 'meeting-buy']);
  const salesCents = soldGoods.reduce((total, item) => total + item.totalCents, 0);
  const stockSpendCents = purchasedGoods.reduce((total, item) => total + item.totalCents, 0);
  const soldQty = soldGoods.reduce((total, item) => total + item.qty, 0);
  const boughtQty = purchasedGoods.reduce((total, item) => total + item.qty, 0);
  const passedOffers = entries.filter((entry) => entry.kind === 'meeting-expired').length;
  const services = new Map();
  for (const entry of entries.filter(item => item.kind === 'service' && item.accepted && item.totalCents > 0)) {
    const key = `${entry.plotIndex}:${entry.facilityId}`;
    const service = services.get(key) || { plotIndex: entry.plotIndex, facilityId: entry.facilityId, name: FACILITY_BY_ID[entry.facilityId]?.name || 'Town service', visits: 0, totalCents: 0 };
    service.visits += 1;
    service.totalCents += entry.totalCents;
    services.set(key, service);
  }
  const earnedServices = [...services.values()];
  const serviceIncomeCents = earnedServices.reduce((total, item) => total + item.totalCents, 0);
  const notes = [];
  if (soldQty) notes.push(`${soldQty} item${soldQty === 1 ? '' : 's'} sold to customers at your prices.`);
  if (boughtQty) notes.push(`${boughtQty} item${boughtQty === 1 ? '' : 's'} restocked from suppliers.`);
  if (boughtQty && !soldQty) notes.push('Stock is ready; no customer bought during this shift. Check prices or work the next shift.');
  if (passedOffers) notes.push(`${passedOffers} optional barter offer${passedOffers === 1 ? '' : 's'} passed while ordinary trade continued.`);
  if (!entries.some((entry) => entry.accepted && (entry.totalCents || entry.kind === 'delivery')) && stopReason === 'limit') notes.push('No customer purchase during this shift. Review the next arrival and your shelf prices.');
  const declinedPrices = entries.filter((entry) => entry.kind === 'refused' && entry.reason === 'Price above willingness').length;
  if (declinedPrices) notes.push(`${declinedPrices} customer request${declinedPrices === 1 ? '' : 's'} passed because your price was too high.`);

  return {
    mode: aggregate ? 'day' : 'customer',
    elapsedMinutes: state.timeMinutes - startMinute,
    receivedCents: entries.reduce((total, entry) => total + Math.max(0, entry.totalCents || 0), 0),
    spentCents: entries.reduce((total, entry) => total - Math.min(0, entry.totalCents || 0), 0),
    cashDeltaCents: state.cashCents - startCash,
    transactions: state.transactions - startTransactions,
    soldGoods, purchasedGoods, salesCents, stockSpendCents, soldQty, boughtQty,
    grossProfitCents: soldGoods.reduce((total, item) => total + item.grossProfitCents, 0),
    grossProfitKnown: soldGoods.every((item) => item.costKnown),
    notes, passedOffers,
    earnedServices, serviceIncomeCents,
    otherIncomeCents: entries.reduce((total, entry) => total + Math.max(0, entry.totalCents || 0), 0) - salesCents - serviceIncomeCents,
    otherSpentCents: entries.reduce((total, entry) => total - Math.min(0, entry.totalCents || 0), 0) - stockSpendCents,
    ...constructionChanges(state, initialConstruction),
    entries, lastEntry: entries.at(-1) || null, stopReason, expiringOffer,
  };
}

export function runTradingDay(state) {
  return runWorkShift(state, { durationMinutes: 1440, aggregate: true });
}
